import React, { useState, useMemo } from 'react';
import '../../style/SidebarZohoCompact.css';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  Home as HomeIcon,
  People as CustomersIcon,
  People as PeopleIcon,
  ChevronLeft as ChevronLeftIcon,
  LocalShipping as TruckIcon,
  Book as PassbookIcon,
  PropaneTank as CylinderIcon,
  Category as GoodsIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Assessment as ReportsIcon,
  Receipt as BillingIcon,
  ReceiptLongOutlined as InvoiceIcon,
  PaymentsOutlined as PaymentIcon,
  ShoppingCart as PurchaseIcon,
  ArrowDownward as PurchaseInwardIcon,
  ArrowUpward as PurchaseOutwardIcon,
  History as PurchaseHistoryIcon,
  AddCircle as CreditNoteIcon,
  PointOfSale as SalesIcon,
  Assignment as SalesOrderIcon,
  Assignment as AssignmentIcon,
  DescriptionOutlined as EstimateIcon,
  Schedule as ScheduleIcon,
  Campaign as CampaignIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
  Dashboard as DashboardIcon,
  ContactPage as ContactPageIcon,
  FilterList as FilterListIcon,
  AutoAwesome as AutoAwesomeIcon,
  Person as PersonIcon,
  Call as CallIcon,
  Mail as MailIcon,
  Receipt as BillsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useColorMode } from '../../contexts/ColorModeContext';
import { TokenManager } from '../../utils/tokenManager';
import { planAtLeast } from '../../utils/planAccess';
import UpgradeModal from '../UpgradeModal';

// ---- Design tokens (kept local so the palette stays consistent across Sidebar/Header) ----
const COLORS = {
  primary: '#4338CA',
  primarySoft: '#EEF2FF',
  primarySoftBorder: '#E0E7FF',
  ink: '#0F172A',
  inkSoft: '#475569',
  muted: '#94A3B8',
  border: '#E7EAF0',
  surface: '#FFFFFF',
  hover: '#F8FAFC',
};

const DARK_COLORS = {
  // Professional navy surface with clear contrast against the dashboard.
  primary: '#818CF8',
  primarySoft: '#252B4A',
  primarySoftBorder: '#3B4570',
  ink: '#F8FAFC',
  inkSoft: '#D1D9E6',
  muted: '#94A3B8',
  border: '#263247',
  surface: '#111827',
  hover: '#1B2538',
};

const menuItems = [
  {
    title: 'Home',
    icon: <HomeIcon />,
    path: '/dashboard',
  },
{
      title: 'CRM',
      icon: <AccountTreeIcon />,
      path: '/crm',
      planRequired: 'gold',
      children: [
        { title: 'CRM Dashboard', path: '/crm/dashboard', icon: <DashboardIcon /> },
        { title: 'My Leads', path: '/crm/my-leads', icon: <PersonIcon />, createPath: '/crm/leads/new' },
        { title: 'IndiaMART Leads', path: '/crm/india-mart-leads', icon: <AutoAwesomeIcon /> },
        // { title: 'Companies', path: '/crm/companies', icon: <BusinessIcon /> },
        { title: 'Contacts', path: '/crm/contacts', icon: <ContactPageIcon /> },
        { title: 'Follow-ups', path: '/crm/followups', icon: <ScheduleIcon /> },
        { title: 'Calls', path: '/crm/calls', icon: <CallIcon /> },
        // { title: 'WhatsApp', path: '/crm/whatsapp', icon: <ChatIcon /> },
        { title: 'Bulk Messages', path: '/crm/bulk-messages', icon: <CampaignIcon /> },
        { title: 'Marketing Campaigns', path: '/crm/marketing-campaigns', icon: <MailIcon /> },
        // { title: 'Templates', path: '/crm/templates', icon: <DescriptionIcon /> },
        // { title: 'Sales Pipeline', path: '/crm/pipeline', icon: <BarChartIcon /> },
        // { title: 'Opportunities', path: '/crm/opportunities', icon: <GavelIcon /> },
        { title: 'Activities', path: '/crm/activities', icon: <TimelineIcon /> },
        { title: 'CRM Reports', path: '/crm/reports', icon: <FilterListIcon /> },
      ],
    },
  {
    title: 'Challans',
    icon: <TruckIcon />,
    path: '/challans',
    children: [
      { title: 'Challan Entry',
        path: '/dashboard/challans/create',
        children: [
          { title: 'Delivered Challan', path: '/dashboard/challans/delivered' },
          { title: 'Received Challan', path: '/dashboard/challans/received' },
          { title: 'Combined Challan', path: '/dashboard/challans/combined' },
        ]
      },
      { title: 'View Challan', path: '/dashboard/challans/view' },
    ],
  },
  {
    title: 'Customers',
    icon: <CustomersIcon />,
    path: '/customers',
    children: [
      { title: 'Create Customer', path: '/dashboard/customers/create' },
      { title: 'View Customers', path: '/dashboard/customers/view' },
    ],
  },
  {
    title: 'Passbook',
    icon: <PassbookIcon />,
    path: '/dashboard/customers/passbook',
  },
  {
    title: 'Sales',
    icon: <SalesIcon />,
    path: '/dashboard/sales',
    planRequired: 'silver',
    children: [
      {
        title: 'Estimates',
        icon: <EstimateIcon />,
        path: '/dashboard/estimates',
        createPath: '/dashboard/estimates/new',
      },
      
          {
            title: 'Sales Orders',
            icon: <SalesOrderIcon />,
            path: '/dashboard/sales-orders',
            createPath: '/dashboard/sales-orders/new',
          },
          {
            title: 'Proforma Invoices',
            icon: <InvoiceIcon />,
            path: '/dashboard/proforma-invoices',
            createPath: '/dashboard/proforma-invoices/new',
          },
          {
            title: 'Customer POs',
            icon: <AssignmentIcon />,
            path: '/dashboard/customer-pos',
            createPath: '/dashboard/customer-pos/new',
          },
        
      {
        title: 'Invoices',
        icon: <InvoiceIcon />,
        path: '/dashboard/invoices/view',
        createPath: '/dashboard/invoices/create',
      },
      {
        title: 'Recurring Invoices',
        icon: <CreditNoteIcon />,
        path: '/dashboard/recurring-list',
        createPath: '/dashboard/recurring',
      },
      {
        title: 'Payments Records',
        icon: <PaymentIcon />,
        path: '/dashboard/payments',
        createPath: '/dashboard/payments/new',
      },
          {
            title: 'Credit Notes',
            icon: <CreditNoteIcon />,
            path: '/dashboard/credit-notes',
            createPath: '/dashboard/credit-notes/new',
          },
          {
            title: 'Debit Notes',
            icon: <CreditNoteIcon />,
            path: '/dashboard/debit-notes',
            createPath: '/dashboard/debit-notes/new',
          },
    ],
  },
  {
    title: 'Purchases',
    icon: <PurchaseIcon />,
    path: '/dashboard/purchases',
    planRequired: 'silver',
    children: [
      {
        title: 'Purchase Inward',
        icon: <PurchaseInwardIcon />,
        path: '/dashboard/purchases/inward',
      },
      {
        title: 'Purchase Outward',
        icon: <PurchaseOutwardIcon />,
        path: '/dashboard/purchases/outward',
      },
      {
        title: 'Purchase History',
        icon: <PurchaseHistoryIcon />,
        path: '/dashboard/purchases/orders',
      },
      {
        title: 'Vendor',
        icon: <PeopleIcon />,
        path: '/dashboard/vendors/view',
        createPath: '/dashboard/vendors/form',
      },
      {
        title: 'Vendor Passbook',
        icon: <PassbookIcon />,
        path: '/dashboard/purchases/vendor-passbook',
      },
      {
        title: 'Bills',
        icon: <BillsIcon />,
        path: '/dashboard/bills',
        createPath: '/dashboard/new-bill',
      },
    ],
  },
  {
    title: 'Billing / Statements',
    icon: <BillingIcon />,
    path: '/dashboard/billing-statements',
  },
  {
    title: 'Cylinders',
    icon: <CylinderIcon />,
    children: [
      { title: 'Cylinder Dashboard', path: '/dashboard/cylinders/cylinderdashboard' },
      { title: 'Cylinder Tracking & History', path: '/dashboard/cylinders/history' },
      { title: 'Cylinder Master', path: '/dashboard/inventory/cylinders' },
    ],
  },
  {
    title: 'Items & Goods',
    icon: <GoodsIcon />,
    path: '/dashboard/inventory/goods',
  },
  {
    title: 'Reports',
    icon: <ReportsIcon />,
    path: '/dashboard/reports',
  },
  {
    title: 'Users',
    icon: <GroupIcon />,
    path: '/dashboard/users',
  },
  {
    title: 'Settings',
    icon: <SettingsIcon />,
    path: '/dashboard/settings',
  },
];

const drawerWidth = 240;

const Sidebar = ({ open, onToggle, mobileOpen, onMobileClose, loading = false }) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const colors = mode === 'dark' ? DARK_COLORS : COLORS;
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: '', requiredPlan: 'silver' });
  const showContent = isDesktop ? open : true;

  const currentPlan = useMemo(() => TokenManager.getPlan(), []);

  const isPlanLocked = (requiredPlan) => !planAtLeast(currentPlan, requiredPlan);

  const getPlanLabel = (requiredPlan) => {
    if (!requiredPlan) return 'PRO';
    const r = requiredPlan.toLowerCase();
    if (r === 'gold') return 'GOLD';
    if (r === 'silver') return 'SILVER';
    return 'PRO';
  };

  React.useEffect(() => {
    const routeMatches = (path) =>
      !!path && (location.pathname === path || location.pathname.startsWith(path + '/'));

    const hasActiveDescendant = (item) => {
      if (routeMatches(item.path)) return true;
      return Array.isArray(item.children)
        ? item.children.some(hasActiveDescendant)
        : false;
    };

    const nextExpanded = {};
    const walk = (items, parentKey = '') => {
      items.forEach((item) => {
        if (!item.children?.length) return;

        const key = parentKey ? `${parentKey}-${item.title}` : item.title;
        if (hasActiveDescendant(item)) nextExpanded[key] = true;

        walk(item.children, key);
      });
    };

    walk(menuItems);
    setExpandedMenus((prev) => ({ ...prev, ...nextExpanded }));
  }, [location.pathname]);

  const handleMenuClick = (title) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) {
      onMobileClose();
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const drawerContent = (
    <Box
      className={`app-sidebar ${mode === 'dark' ? 'app-sidebar-dark' : 'app-sidebar-light'}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: colors.surface,
        color: colors.ink,
        '& .MuiListItemText-primary': { color: `${colors.ink} !important` },
        '& .MuiListItemIcon-root': { color: colors.inkSoft },
        '& .MuiSvgIcon-root': { color: 'inherit' },
        '& .MuiList-root': { scrollbarWidth: 'thin', scrollbarColor: `${colors.border} transparent` },
        '& .MuiList-root::-webkit-scrollbar': { width: 5 },
        '& .MuiList-root::-webkit-scrollbar-thumb': { backgroundColor: colors.border, borderRadius: 8 },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          height: 64,
          minHeight: 64,
          px: showContent ? 2.5 : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showContent ? 'space-between' : 'center',
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
          backgroundColor: colors.surface,
        }}
      >
        <Box
          onClick={() => handleNavigation('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          {/* Brand mark */}
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.primary,
              color: '#fff',
              fontWeight: 800,
              fontSize: '0.95rem',
              letterSpacing: '-0.02em',
            }}
          >
            Z
          </Box>

          {showContent && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              style={{ minWidth: 0 }}
            >
              <Typography
                className="sidebar-brand-title"
                sx={{
                  '&&': { color: mode === 'dark' ? '#FFFFFF !important' : `${colors.ink} !important` },
                  color: mode === 'dark' ? '#FFFFFF' : colors.ink,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                }}
              >
                Zerlak One
              </Typography>
              <Typography
                className="sidebar-brand-subtitle"
                sx={{
                  '&&': { color: mode === 'dark' ? '#FFFFFF !important' : `${colors.muted} !important` },
                  color: colors.muted,
                  fontWeight: 500,
                  fontSize: '0.6875rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Business Suite
              </Typography>
            </motion.div>
          )}
        </Box>

        {/* Only show close button on mobile */}
        {!isDesktop && (
          <IconButton
            onClick={onMobileClose}
            size="small"
            sx={{
              color: colors.inkSoft,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: colors.hover,
              },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <List sx={{ flex: 1, py: 0.75, px: showContent ? 1 : 0.75, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          // Skeleton loading state
          [...Array(8)].map((_, index) => (
            <Box key={index} sx={{ mb: 0.25 }}>
              <ListItem disablePadding>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: showContent ? 1 : 0,
                    py: 0,
                    minHeight: 34,
                    borderRadius: '6px',
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    sx={{ flexShrink: 0 }}
                  />
                  {showContent && (
                    <Skeleton
                      variant="text"
                      width={120}
                      height={20}
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>
              </ListItem>
            </Box>
          ))
        ) : (
          // Actual menu items
          menuItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedMenus[item.title];
            const isItemActive = item.path ? isActive(item.path) : false;
            const locked = isPlanLocked(item.planRequired);

            return (
              <Box key={item.title}>
                <ListItem disablePadding sx={{ mb: 0.125 }}>
                  <ListItemButton
                    onClick={() => {
                      if (locked) {
                        setUpgradeModal({ open: true, feature: item.title, requiredPlan: item.planRequired || 'silver' });
                        return;
                      }
                      if (hasChildren) {
                        handleMenuClick(item.title);
                      } else if (item.path) {
                        handleNavigation(item.path);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: '6px',
                      backgroundColor: isItemActive ? colors.primarySoft : 'transparent',
                      '&:hover': {
                        backgroundColor: isItemActive ? colors.primarySoft : colors.hover,
                        '& .MuiListItemText-primary': {
                          color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                        },
                        '& .MuiListItemIcon-root': {
                          color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                        },
                        '& .MuiSvgIcon-root': {
                          color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                        },
                      },
                      minHeight: 34,
                      height: 34,
                      py: 0,
                      px: showContent ? 1 : 0,
                      '& .MuiTouchRipple-root': { display: 'none' },
                      justifyContent: showContent ? 'flex-start' : 'center',
                      transition: 'background-color 0.15s ease',
                      '&::before': isItemActive ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: '3px',
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: colors.primary,
                      } : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isItemActive ? colors.primary : colors.inkSoft,
                        minWidth: showContent ? 28 : 'auto',
                        width: showContent ? 28 : 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        mr: showContent ? 0.25 : 0,
                        '& svg': { fontSize: '1.08rem' },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {showContent && (
                      <>
                        <ListItemText
                          primary={item.title}
                          sx={{
                            color: locked ? '#CBD5E1' : (mode === 'dark' ? '#F8FAFC' : (isItemActive ? colors.primary : colors.ink)),
                            '& .MuiTypography-root': {
                              fontWeight: isItemActive ? 600 : 500,
                              fontSize: '0.80rem',
                              color: `${mode === 'dark' ? '#F8FAFC' : (isItemActive ? colors.primary : colors.ink)} !important`,
                            },
                          }}
                        />
                         {locked && (
                           <Chip label={getPlanLabel(item.planRequired)} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E', '& .MuiChip-label': { px: 0.5 } }} />
                         )}
                        {hasChildren && (
                          <ExpandMore
                            sx={{
                              color: isItemActive ? colors.primary : colors.muted,
                              fontSize: '0.95rem',
                              ml: 'auto',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                            }}
                          />
                        )}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Nested Menu Items */}
                {hasChildren && isExpanded && showContent && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding className="zoho-submenu" sx={{ pl: 1, borderLeft: `1px solid ${colors.border}`, ml: 2.1, my: 0.125 }}>
                      {item.children.map((child) => {
                        const hasNestedChildren = child.children && child.children.length > 0;
                        const isChildExpanded = expandedMenus[`${item.title}-${child.title}`];
                        const isChildActive = isActive(child.path);

                        return (
                          <Box key={child.title}>
                            <ListItem disablePadding className="zoho-child-item" sx={{ mb: 0.125 }}>
                              <Box
                                className={`zoho-child-row ${isChildActive ? 'is-active' : ''}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'stretch',
                                  width: '100%',
                                  minHeight: 32,
                                  height: 32,
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  backgroundColor: isChildActive ? colors.primarySoft : 'transparent',
                                  '&:hover': {
                                    backgroundColor: isChildActive ? colors.primarySoft : colors.hover,
                                    '& .MuiTypography-root': {
                                      color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                    },
                                    '& .MuiSvgIcon-root': {
                                      color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                    },
                                  },
                                }}
                              >
                                <ListItemButton
                                  onClick={() => {
                                    if (hasNestedChildren) {
                                      setExpandedMenus((prev) => ({
                                        ...prev,
                                        [`${item.title}-${child.title}`]: !prev[`${item.title}-${child.title}`],
                                      }));
                                    } else if (child.path) {
                                      handleNavigation(child.path);
                                    }
                                  }}
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 32,
                                    height: 32,
                                    py: 0,
                                    px: 1.25,
                                    borderRadius: 0,
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                      '& .MuiTypography-root': {
                                        color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                      },
                                    },
                                    '& .MuiTouchRipple-root': { display: 'none' },
                                  }}
                                >
                                  <ListItemText
                                    primary={child.title}
                                    sx={{
                                      m: 0,
                                      minWidth: 0,
                                      color: isChildActive ? colors.primary : colors.inkSoft,
                                      '& .MuiTypography-root': {
                                        fontWeight: isChildActive ? 600 : 500,
                                        fontSize: '0.78rem',
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: `${isChildActive ? colors.primary : colors.inkSoft} !important`,
                                      },
                                    }}
                                  />

                                  {hasNestedChildren && (
                                    <ExpandMore
                                      sx={{
                                        ml: 'auto',
                                        color: isChildActive ? colors.primary : colors.muted,
                                        fontSize: '0.9rem',
                                        transform: isChildExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.18s',
                                      }}
                                    />
                                  )}
                                </ListItemButton>

                                {child.createPath && (
                                  <Box
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Create ${child.title}`}
                                    title={`Create ${child.title}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNavigation(child.createPath);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNavigation(child.createPath);
                                      }
                                    }}
                                    className="zoho-plus-action"
                                    sx={{
                                      color: mode === 'dark' ? '#818CF8' : undefined,
                                      '&:hover': {
                                        color: mode === 'dark' ? '#FFFFFF' : undefined,
                                        backgroundColor: mode === 'dark' ? '#252E4A' : undefined,
                                      },
                                    }}
                                  >
                                    +
                                  </Box>
                                )}
                              </Box>
                            </ListItem>

                            {/* Nested Children (Second Level) */}
                            {hasNestedChildren && isChildExpanded && (
                              <Collapse in={isChildExpanded} timeout="auto" unmountOnExit>
                                <List
                                  component="div"
                                  disablePadding
                                  sx={{ pl: 1.75 }}
                                >
                                  {child.children.map((nestedChild) => {
                                    const nestedActive = isActive(nestedChild.path);

                                    return (
                                      <ListItem key={nestedChild.title} disablePadding sx={{ mb: 0.125 }}>
                                        <ListItemButton
                                          onClick={() => handleNavigation(nestedChild.path)}
                                          sx={{
                                            borderRadius: '5px',
                                            backgroundColor: nestedActive ? colors.primarySoft : 'transparent',
                                            '&:hover': {
                                              backgroundColor: nestedActive ? colors.primarySoft : colors.hover,
                                              '& .MuiListItemText-primary': {
                                                color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                              },
                                              '& .MuiListItemIcon-root, & .MuiSvgIcon-root': {
                                                color: mode === 'dark' ? '#FFFFFF !important' : undefined,
                                              },
                                            },
                                            minHeight: 28,
                                            height: 28,
                                            py: 0,
                                            px: 1,
                                            transition: 'background-color 0.15s ease',
                                          }}
                                        >
                                          {nestedChild.icon && (
                                            <ListItemIcon
                                              sx={{
                                                minWidth: 32,
                                                mr: 0.75,
                                                color: nestedActive
                                                  ? colors.primary
                                                  : colors.inkSoft,
                                                '& svg': { fontSize: '1rem' },
                                              }}
                                            >
                                              {nestedChild.icon}
                                            </ListItemIcon>
                                          )}

                                          <ListItemText
                                            primary={nestedChild.title}
                                            sx={{
                                              color: nestedActive ? colors.primary : colors.inkSoft,
                                              '& .MuiTypography-root': {
                                                fontWeight: nestedActive ? 600 : 500,
                                                fontSize: '0.73rem',
                                                color: `${nestedActive ? colors.primary : colors.inkSoft} !important`,
                                              },
                                            }}
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            )}
                          </Box>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })
        )}
      </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: showContent ? 2 : 1,
          borderTop: `1px solid ${colors.border}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {showContent ? (
          <Typography
            className="sidebar-footer-text"
            sx={{
              '&&': { color: mode === 'dark' ? '#FFFFFF !important' : `${colors.muted} !important` },
              color: mode === 'dark' ? '#FFFFFF' : colors.muted,
              fontSize: '0.7rem',
              fontWeight: 500,
              textAlign: 'center',
              display: 'block',
            }}
          >
            © 2024 Zerlak One
          </Typography>
        ) : (
          <Typography className="sidebar-footer-symbol" sx={{ '&&': { color: mode === 'dark' ? '#FFFFFF !important' : `${colors.muted} !important` }, color: mode === 'dark' ? '#FFFFFF' : colors.muted, fontSize: '0.65rem', fontWeight: 600 }}>©</Typography>
        )}
      </Box>
    </Box>
  );

  const handleUpgrade = async (newPlan) => {
    try {
      const { subscriptionApi } = await import('../../api/subscriptionApi');
      await subscriptionApi.updatePlan(newPlan);
      localStorage.setItem('user_plan', newPlan);
      window.location.reload();
    } catch (err) {
      console.error('Upgrade failed:', err);
    }
    setUpgradeModal({ open: false, feature: '', requiredPlan: 'silver' });
  };

  const modalContent = (
    <UpgradeModal
      open={upgradeModal.open}
      onClose={() => setUpgradeModal({ open: false, feature: '', requiredPlan: 'silver' })}
      currentPlan={currentPlan}
      lockedFeature={upgradeModal.feature}
      onUpgrade={handleUpgrade}
    />
  );

  if (isDesktop) {
    return (
      <>
        <Drawer variant="permanent" open={open} sx={{
        width: open ? drawerWidth : 72,
        flexShrink: 0,
        zIndex: 200,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : 72,
          boxSizing: 'border-box',
          backgroundColor: colors.surface,
          color: mode === 'dark' ? '#F8FAFC' : colors.ink,
          borderRight: `1px solid ${colors.border}`,
          boxShadow: 'none',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          overflowY: 'auto',
        },
      }}>
        {drawerContent}
      </Drawer>
      {modalContent}
      </>
    );
  }

  return (
    <>
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', lg: 'none' },
        zIndex: 200,
        '& .MuiDrawer-paper': { width: drawerWidth, backgroundColor: colors.surface, color: mode === 'dark' ? '#F8FAFC' : colors.ink, borderRight: `1px solid ${colors.border}`, boxShadow: '4px 0 24px rgba(15, 23, 42, 0.08)' },
      }}>
      {drawerContent}
    </Drawer>
    {modalContent}
    </>
  )
};

export default Sidebar;
